import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'

import IconButton from 'particles/Button/IconButton'
import Text from 'particles/Text'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import Tooltip from 'particles/Tooltip'
import { spacing } from 'particles/styles'
import CommentContainer from 'components/Shared/Comment'
import { NumberInputContainer, ReadOnlyContainer, Wrapper } from './style'
import { useField, useFormikContext } from 'formik'

export type NumberInputSize = 'm' | 's'

export interface NumberInputProps {
    error?: string
    // enable error border
    showErrorStyle?: boolean
    isCommentable?: boolean
    isCommented?: boolean
    isDisabled?: boolean
    isReadOnly?: boolean
    isRequired?: boolean
    label?: string
    min?: number
    name?: string
    onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    onFocus?: () => void
    onLabelClick?: () => void
    placeholder?: string
    prefix?: string | null
    size?: NumberInputSize
    subtext?: string
    tooltipContext?: { title?: string; label?: string }
    value?: string
    formattedValue?: string
    width?: string
    className?: string
    identifier?: string
    labelSize?: LabelSize
    link?: LinkConfig
    labelTooltip?: string

    // using this default naming type, since there are already used in multiple number input test id
    // converting naming into `dataTestId` will require changes in multiple file later
    ['data-testid']?: string
}

interface SizeMap {
    height: string
    paddingX: string
    paddingY: string
    gap: string
}
const sizeMap: Record<NumberInputSize, SizeMap> = {
    m: {
        height: '40px',
        paddingX: '11px',
        paddingY: '10px',
        gap: spacing[200],
    },
    s: {
        height: '32px',
        paddingX: '7px',
        paddingY: '6px',
        gap: spacing[200],
    },
}

export const NumberInput: React.FC<NumberInputProps> = ({
    label = '',
    name = '',
    subtext,
    isRequired = false,
    size = 's',
    isCommentable = false,
    isCommented = false,
    onLabelClick,
    tooltipContext,
    isReadOnly = false,
    isDisabled = false,
    error,
    showErrorStyle = false,
    placeholder = '',
    prefix = '',
    value,
    onChange,
    formattedValue,
    width,
    className,
    identifier,
    labelSize,
    link,
    labelTooltip,
    ...inputProps
}) => {
    const [inputValue, setInputValue] = useState<string>(value ?? '')
    const [t] = useTranslation()
    const { height, paddingX, paddingY, gap } = sizeMap[size]

    useEffect(() => {
        if (value !== undefined) setInputValue(value)
    }, [value])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value)
        onChange?.(e)
    }

    const readOnlyValue = (() => {
        if (formattedValue) {
            return formattedValue
        }

        if (value !== undefined) {
            return `${prefix} ${inputValue}`
        }
        if (inputValue === null || inputValue === undefined) {
            return
        }
        if (prefix) {
            return `${prefix} ${inputValue}`
        }
        return inputValue
    })()

    const preventChangeOnWheel = (e: React.WheelEvent<HTMLInputElement>): void => {
        const target = e.target as HTMLInputElement

        // Prevent input value change
        target.blur()

        // Prevent page/container scrolling
        e.stopPropagation()

        // Refocus immediately, on the next tick (after the current function is done)
        setTimeout(() => {
            target.focus()
        }, 0)
    }

    return (
        <Wrapper gap={gap} className={className}>
            {(label || subtext) && (
                <CommentContainer identifier={identifier}>
                    <Label
                        title={label}
                        subtext={subtext}
                        isRequired={isRequired}
                        size={labelSize ? labelSize : size}
                        isCommentable={isCommentable}
                        isCommented={isCommented}
                        onClick={onLabelClick}
                        link={link}
                        tooltip={labelTooltip}
                    />
                </CommentContainer>
            )}

            {isReadOnly ? (
                <ReadOnlyContainer
                    width={width}
                    hasValue={!!readOnlyValue}
                    height={height}
                    padding={`${paddingY} ${paddingX}`}
                >
                    <Text
                        scale={!!readOnlyValue ? 'bodyS700' : 'bodyS400'}
                        color={!!readOnlyValue ? 'primary' : 'disable'}
                    >
                        {readOnlyValue || t('placeholderText.noDetailsAdded')}
                    </Text>
                </ReadOnlyContainer>
            ) : (
                <>
                    <NumberInputContainer
                        isDisabled={isDisabled}
                        hasError={showErrorStyle || !!error}
                        height={height}
                        padding={paddingX}
                        width={width}
                    >
                        <Text scale="bodyS400" color={isDisabled ? 'disable' : 'primary'}>
                            {prefix}
                        </Text>
                        <input
                            name={name}
                            value={inputValue}
                            className="number-input"
                            type="number"
                            disabled={isDisabled}
                            placeholder={placeholder}
                            onChange={handleInputChange}
                            onWheel={preventChangeOnWheel}
                            {...inputProps}
                        />
                        {isDisabled && (tooltipContext?.title || tooltipContext?.label) && (
                            <Tooltip placement="bottom" title={tooltipContext?.title} label={tooltipContext?.label}>
                                <IconButton
                                    icon="question"
                                    size="xs"
                                    form="icon"
                                    color="neutral"
                                    className="tooltip-icon"
                                />
                            </Tooltip>
                        )}
                    </NumberInputContainer>
                    {error && (
                        <Text
                            data-testid={`${inputProps['data-testid']}_error`}
                            scale="caption400"
                            color="error"
                            className="error"
                            as="div"
                            style={{ marginTop: 0 }}
                        >
                            {error}
                        </Text>
                    )}
                </>
            )}
        </Wrapper>
    )
}

export default NumberInput

interface FormikNumberInputProps extends NumberInputProps {
    name: string
    showError?: boolean // set false if want to use external error state to control the error display
}

const FormikNumberInput: React.FC<FormikNumberInputProps> = ({ name, showError = true, onChange, ...props }) => {
    const [field, meta] = useField(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(e)
            if (!meta.touched) setFieldTouched(field.name, true, false)
            return
        }
        setFieldValue(name, e.target.value === '' ? null : +e.target.value)
        if (!meta.touched) setFieldTouched(field.name, true, false)
    }

    return (
        <NumberInput
            {...props}
            name={name}
            onChange={handleChange}
            error={showError ? meta.error : undefined}
            value={field.value}
        />
    )
}

export { FormikNumberInput }
